// src/oura/merchant/setup/AccessSteps.tsx
//
// The three access steps on the guided Setup page: the Google Business
// Profile, the website, and Search Console with Analytics. Each says why we
// ask, shows the steps for their own platform, and has the one button that
// moves it on. Done, it says so in green and offers Manage, which opens the
// drawer where access is removed.

import { useState, type ReactNode } from "react";
import { CheckIcon, ExternalLinkIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { safeConnectRedirect } from "../../lib/safeRedirect";
import { PillButton, PillLink, SURFACE, Seg, TYPE } from "@oura/ui";
import {
    claimMyAccess,
    connectMyAccess,
    type AccessItem,
    type MerchantConnectResult,
    type MerchantOnboarding,
    type SiteBuilder,
} from "@service/aeo/onboarding";
import { dayMonth, isHeld } from "@oura/onboarding/derive";
import type { MerchantStep } from "@oura/onboarding/oneSetup";
import { ACCESS_WHY, ADD_USER_STEPS, GBP_STEPS, GOOGLE_WAITING, MERCHANT_ACCESS_NAME, SITE_BUILDER_ORDER, SITE_GUIDES } from "@oura/onboarding/guides";
import { AccessAddress, GuideSteps } from "@oura/onboarding/StepText";
import { AccessStatusLine } from "../AccessDrawer";

const NOT_SAVED = "That did not go through. Try again in a moment.";
const NO_GOOGLE_SCREEN = "We could not open Google's connection screen. Try again in a moment.";
/** Where an owner opens their profile. Google picks the right one once they are signed in. */
const GBP_HOME = "https://business.google.com/locations";
/** Taller pills on a phone, where they are tapped. */
const TAP = "max-sm:h-9";

export interface AccessStepProps {
    step: MerchantStep;
    onboarding: MerchantOnboarding;
    readOnly: boolean;
    /** `advance`: the merchant finished their part, so the page moves to the next step. */
    onChanged: (updated: MerchantOnboarding, advance: boolean) => void;
    /** Opens the drawer for one held access: how to remove us. */
    onManage: (item: AccessItem) => void;
    /** Staff confirming this for the merchant from a view-as session (#4510). */
    onBehalf?: boolean;
}

/** A press that saves, with its own busy state and a sentence when it fails. */
function useRun() {
    const [busy, setBusy] = useState<string | null>(null);
    const [failure, setFailure] = useState<string | null>(null);
    const run = async (which: string, action: () => Promise<void>) => {
        setBusy(which);
        setFailure(null);
        try {
            await action();
        } catch {
            setFailure(NOT_SAVED);
        } finally {
            setBusy(null);
        }
    };
    return { busy, failure, setFailure, setBusy, run };
}

function Failure({ text }: { text: string | null }) {
    if (!text) return null;
    return (
        <p role="alert" className={cn(TYPE.tc, "m-0 rounded-xl bg-rose-50 px-3 py-2 font-medium text-rose-700")}>
            {text}
        </p>
    );
}

/** The green band a finished access step shows instead of its instructions. */
function HeldBand({ item, onManage }: { item: AccessItem; onManage: () => void }) {
    const since = dayMonth(item.since);
    return (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-emerald-50 px-4 py-3">
            <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
                <CheckIcon size={15} strokeWidth={3} />
            </span>
            <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-emerald-900")}>
                <span className="font-medium">{MERCHANT_ACCESS_NAME[item.key]}</span>
                {since ? `, since ${since}.` : "."}
                {item.detail && <span className="block text-[13px] leading-[18px] text-emerald-800">{item.detail}</span>}
            </p>
            <PillButton className={TAP} onClick={onManage}>
                Manage
            </PillButton>
        </div>
    );
}

/** Their part is done and the invite is with us: what happens now, and a way back. */
function WaitingBand({ busy, disabled, onUndo }: { busy: boolean; disabled: boolean; onUndo: () => void }) {
    return (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-blue-50 px-4 py-3">
            <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-blue-900")}>
                <span className="font-medium">Thanks, that is your part done.</span> We look for your invite and confirm it here.
            </p>
            <PillButton className={TAP} busy={busy} writes disabled={disabled} onClick={onUndo}>
                Undo
            </PillButton>
        </div>
    );
}

/** Part one, what to do there: a titled grey block with the numbered steps, the address, and any link out. */
function HowTo({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className={cn(SURFACE.inner, "px-4 py-4")}>
            <p className={cn(TYPE.tb, "m-0 mb-1 font-semibold text-gray-900")}>{title}</p>
            {children}
        </div>
    );
}

/** Part two, tell us: one line and the one blue button, set apart so it reads as the last thing to press. */
function Confirm({ question, onBehalf = false, children }: { question: string; onBehalf?: boolean; children: ReactNode }) {
    return (
        <div className={cn("flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3 ring-1 ring-inset", onBehalf ? "bg-blue-50 ring-blue-200" : "ring-black/[0.06]")}>
            <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1", onBehalf ? "text-blue-900" : "text-gray-700")}>
                {onBehalf ? "You are confirming this for the client. Nobody is emailed or notified." : question}
            </p>
            {children}
        </div>
    );
}

export function GbpStep({ step, onboarding, readOnly, onChanged, onManage, onBehalf }: AccessStepProps) {
    const item = step.items[0];
    const { busy, failure, run } = useRun();
    if (isHeld(item)) return <HeldBand item={item} onManage={() => onManage(item)} />;
    if (item.status === "claimed") {
        return (
            <div className="space-y-3">
                <WaitingBand busy={busy === "undo"} disabled={readOnly} onUndo={() => void run("undo", async () => onChanged(await claimMyAccess("gbp", false), false))} />
                <Failure text={failure} />
            </div>
        );
    }
    return (
        <div className="space-y-4">
            <HowTo title="Add us on Google">
                <GuideSteps steps={GBP_STEPS} />
                <AccessAddress className="mt-2 bg-white ring-1 ring-inset ring-black/[0.06]" />
                {onboarding.outlets > 1 && (
                    <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>Do this once for each outlet. You have {onboarding.outlets}.</p>
                )}
                <PillLink href={GBP_HOME} target="_blank" rel="noopener noreferrer" icon={ExternalLinkIcon} className={cn("mt-3", TAP)}>
                    Open my Google profile
                </PillLink>
            </HowTo>
            <Confirm question="Done it? Tell us and we check it for you." onBehalf={onBehalf}>
                <PillButton
                    variant="primary"
                    className={TAP}
                    busy={busy === "claim"}
                    writes
                    disabled={readOnly}
                    onClick={() => void run("claim", async () => onChanged(await claimMyAccess("gbp", true), true))}
                >
                    I've added you
                </PillButton>
            </Confirm>
            <Failure text={failure} />
        </div>
    );
}

export function WebsiteStep({ step, onboarding, readOnly, onChanged, onManage, onBehalf }: AccessStepProps) {
    const item = step.items[0];
    const [builder, setBuilder] = useState<SiteBuilder>(onboarding.siteBuilder ?? "wix");
    const { busy, failure, setFailure, run } = useRun();
    const guide = SITE_GUIDES[builder];
    if (isHeld(item)) return <HeldBand item={item} onManage={() => onManage(item)} />;
    if (item.status === "claimed") {
        return (
            <div className="space-y-3">
                <WaitingBand busy={busy === "undo"} disabled={readOnly} onUndo={() => void run("undo", async () => onChanged(await claimMyAccess("website", false), false))} />
                <Failure text={failure} />
            </div>
        );
    }
    return (
        <div className="space-y-4">
            <div>
                <p className={cn(TYPE.tb, "m-0 mb-2 font-semibold text-gray-900")}>What is your website built on?</p>
                <Seg<SiteBuilder>
                    ariaLabel="What your website is built on"
                    className="max-sm:[&_button]:h-9"
                    value={builder}
                    onChange={(next) => {
                        setBuilder(next);
                        setFailure(null);
                    }}
                    items={SITE_BUILDER_ORDER.map((k) => ({ key: k, label: SITE_GUIDES[k].short }))}
                />
            </div>
            <HowTo title={`Add us in ${guide.short === "Other" ? "your website" : guide.short}`}>
                <GuideSteps steps={guide.steps} />
                <AccessAddress className="mt-2 bg-white ring-1 ring-inset ring-black/[0.06]" />
            </HowTo>
            <Confirm question="Done it? Tell us and we check it for you." onBehalf={onBehalf}>
                <PillButton
                    variant="primary"
                    className={TAP}
                    busy={busy === "claim"}
                    writes
                    disabled={readOnly}
                    onClick={() => void run("claim", async () => onChanged(await claimMyAccess("website", true, builder), true))}
                >
                    {guide.done}
                </PillButton>
            </Confirm>
            <Failure text={failure} />
        </div>
    );
}

/**
 * Search Console and Analytics: Google's own approval screen, one row each.
 * Connecting leaves the page for Google's screen and comes back to it, so the
 * button stays busy until the page goes. Where the backend lists the claim, each
 * row also says "I've added you" after adding us as a read-only user (#4513).
 */
export function GoogleStep({ step, onboarding, readOnly, onChanged, onManage, onBehalf }: AccessStepProps) {
    const { busy, failure, setFailure, setBusy, run } = useRun();

    const connect = async (item: AccessItem) => {
        setBusy(item.key);
        setFailure(null);
        let result: MerchantConnectResult;
        try {
            result = await connectMyAccess(item.key, {});
        } catch {
            result = { ok: false, message: NOT_SAVED };
        }
        if (!result.ok) {
            setFailure(result.message);
            setBusy(null);
            return;
        }
        if (result.kind === "redirect") {
            // Only an address we expect is followed.
            const to = safeConnectRedirect(result.url);
            if (!to) {
                setFailure(NO_GOOGLE_SCREEN);
                setBusy(null);
                return;
            }
            try {
                window.location.assign(to);
            } catch {
                setFailure(NO_GOOGLE_SCREEN);
                setBusy(null);
            }
            return;
        }
        // Both held: their part is done, so the page moves on.
        const after = result.onboarding.access.filter((a) => a.key === "gsc" || a.key === "ga4");
        onChanged(result.onboarding, after.length > 0 && after.every(isHeld));
        setBusy(null);
    };

    return (
        <div className="space-y-4">
            <ul className={cn("list-plain m-0 divide-y overflow-hidden rounded-2xl p-0 ring-1 ring-inset ring-black/[0.06]", "divide-gray-100")}>
                {step.items.map((item) => {
                    const held = isHeld(item);
                    const waiting = item.status === "claimed";
                    // "Or add us as a user": only when the backend lists the claim (#4513). A claim in view-as is staff's, like the website's.
                    const addUser = !held && !waiting && onboarding.claimableAccess?.includes(item.key) === true;
                    const claimDisabled = readOnly && !onBehalf;
                    const steps = item.key === "gsc" ? ADD_USER_STEPS.gsc : ADD_USER_STEPS.ga4;
                    return (
                        <li key={item.key} className={cn("flex flex-wrap items-center gap-3 px-4 py-3", held && "bg-emerald-50")}>
                            {/* On a phone the button drops under the words instead of squeezing them. */}
                            <div className="min-w-0 flex-1 basis-56">
                                <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{MERCHANT_ACCESS_NAME[item.key]}</p>
                                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                                    {held ? <AccessStatusLine item={item} /> : waiting ? GOOGLE_WAITING : ACCESS_WHY[item.key]}
                                </p>
                            </div>
                            {held ? (
                                <PillButton className={TAP} onClick={() => onManage(item)}>
                                    Manage
                                </PillButton>
                            ) : (
                                <>
                                    <PillButton variant="primary" className={cn(TAP, "max-sm:w-full max-sm:justify-center")} busy={busy === item.key} writes disabled={readOnly || busy !== null} onClick={() => void connect(item)}>
                                        {item.key === "gsc" ? "Connect Search Console" : item.key === "ga4" ? "Connect Analytics" : "Connect Google"}
                                    </PillButton>
                                    {waiting && (
                                        <PillButton className={TAP} busy={busy === `undo:${item.key}`} writes disabled={claimDisabled || busy !== null} onClick={() => void run(`undo:${item.key}`, async () => onChanged(await claimMyAccess(item.key, false), false))}>
                                            Undo
                                        </PillButton>
                                    )}
                                </>
                            )}
                            {addUser && (
                                <div className="basis-full">
                                    <HowTo title={`Or add us as a user on ${MERCHANT_ACCESS_NAME[item.key]}`}>
                                        <GuideSteps steps={steps} />
                                        <AccessAddress className="mt-2 bg-white ring-1 ring-inset ring-black/[0.06]" />
                                    </HowTo>
                                    <div className="mt-3">
                                        <Confirm question="Added us? Tell us and we check it for you." onBehalf={onBehalf}>
                                            <PillButton variant="primary" className={TAP} busy={busy === `claim:${item.key}`} writes disabled={claimDisabled || busy !== null} onClick={() => void run(`claim:${item.key}`, async () => onChanged(await claimMyAccess(item.key, true), false))}>
                                                I've added you
                                            </PillButton>
                                        </Confirm>
                                    </div>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                Google asks you to pick the account that owns them and approve read-only access. We can see the numbers. We can never change anything.
            </p>
            <Failure text={failure} />
        </div>
    );
}
